import { useEffect, useState } from 'react';

import type { Channel } from 'stream-chat';
import type { TChatMessage } from '@/services/chat';

import { toChatMessage } from '@/utils/chat';
import { logger } from '@/services/logger';

type TThreadState = {
  messages: TChatMessage[];
  isSending: boolean;
  onSend: (body: string) => Promise<boolean>;
};

const useThread = (channel: Channel | null, staffID: string): TThreadState => {
  const [messages, setMessages] = useState<TChatMessage[]>([]);
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    if (!channel) {
      setMessages([]);

      return undefined;
    }

    const sync = () => {
      setMessages(channel.state.messages.map(message => toChatMessage(message, staffID)));
    };

    sync();
    channel.markRead();

    const listener = channel.on('message.new', sync);

    return () => {
      listener.unsubscribe();
    };
  }, [channel, staffID]);

  const onSend = async (body: string): Promise<boolean> => {
    if (!channel || body.trim().length === 0) {
      return false;
    }

    setIsSending(true);

    try {
      await channel.sendMessage({ text: body.trim() });

      return true;
    } catch (error) {
      logger.error('useThread.onSend', error);

      return false;
    } finally {
      setIsSending(false);
    }
  };

  return { messages, isSending, onSend };
};

export type {
  TThreadState,
};
export {
  useThread,
};
